import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@apollo/client/react";
import type { ResultOf } from "@graphql-typed-document-node/core";
import type { OrgChartItem } from "@salestrak/ui";

import { connectionRows, OrgChartUsersDocument } from "#app/lib/data";
import type { LiveRefetchContext } from "#app/lib/subscriptions/live-workspace-sync";

/** One charted member: the bootstrap's member row plus `roles`, which only
 *  the chart's query selects (the node card's "Role" view option). */
export type OrgChartUser = ResultOf<
  typeof OrgChartUsersDocument
>["users"]["edges"][number]["node"];

// A node is either a real member or the synthetic "No manager" stub that
// tops off the focused person's reporting line when they have no manager.
export type OrgChartNodeData =
  | { kind: "user"; user: OrgChartUser }
  | { kind: "placeholder" };

export type OrgChartUserItem = OrgChartItem<OrgChartNodeData>;

const NO_MANAGER_ID = "__org-chart-no-manager__";

// Suspended/expired/declined/left members drop off the chart entirely — their
// reports re-root rather than dangling under someone who can't log in.
const CHARTED_STATUS_LIST = ["active", "invited"] as const;
const CHARTED_STATUSES: ReadonlySet<string> = new Set(CHARTED_STATUS_LIST);

// One PAGE of the charted pool, not a cap — `useChartedUsers` keeps fetching
// until `total` is exhausted.
const POOL_VARS = {
  limit: 500,
  where: { memberStatus: { in: [...CHARTED_STATUS_LIST] } },
};

interface ChartedPool {
  users: OrgChartUser[];
  /** True once EVERY page of the filtered pool is in. A partial pool would
   * mis-root nodes whose manager sits on a page still in flight, so gates
   * hold for the whole assembly, not just the first response. */
  ready: boolean;
}

/**
 * The chart's member pool — its own status-filtered query, not the
 * WorkspaceData bootstrap: that query stops at 500 rows across every status,
 * so a large workspace silently dropped members from the tree. `teamId`
 * narrows the pool to one team's roster (the team chart's slice).
 */
function useChartedUsers(skip: boolean, teamId?: string): ChartedPool {
  const variables = useMemo(
    () =>
      teamId
        ? {
            ...POOL_VARS,
            where: { ...POOL_VARS.where, teamIds: { hasSome: [teamId] } },
          }
        : POOL_VARS,
    [teamId],
  );
  // Declared before the query so its live re-read can clear it.
  const requestedPageRef = useRef<string | null>(null);
  // A live change re-reads the first page only, so the guard must let the
  // pagination effect below request the later pages again.
  const refetchRef = useRef<() => Promise<unknown>>(() => Promise.resolve());
  const context = useMemo(
    (): LiveRefetchContext => ({
      liveRefetch: () => {
        requestedPageRef.current = null;
        return refetchRef.current();
      },
    }),
    [],
  );
  const { data, loading, fetchMore, refetch } = useQuery(OrgChartUsersDocument, {
    variables,
    // Flips `loading` during fetchMore so the pagination effect can't
    // double-request a page.
    notifyOnNetworkStatusChange: true,
    skip,
    context,
  });
  refetchRef.current = refetch;

  const pool = data?.users;
  // Each offset is requested once per pool. The effect re-runs on every
  // `loading` flip, so without this a failing page would retry in a tight
  // loop; a successful page grows `edges`, which moves the offset past the
  // guard. The key carries the team scope so switching teams (same mounted
  // hook, new variables) can re-request an offset the old pool already used.
  useEffect(() => {
    if (loading || !pool || pool.edges.length >= pool.total) return;
    const skip = pool.edges.length;
    const pageKey = `${teamId ?? ""}:${skip}`;
    if (requestedPageRef.current === pageKey) return;
    requestedPageRef.current = pageKey;
    fetchMore({
      variables: { skip },
      // The users cache is per-page (keyArgs include skip/limit), so page
      // accumulation lives in this query's local view.
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.users) return prev;
        if (!prev?.users) return fetchMoreResult;
        return {
          ...prev,
          users: {
            ...fetchMoreResult.users,
            edges: [...prev.users.edges, ...fetchMoreResult.users.edges],
          },
        };
      },
      // A dropped page leaves `ready` false (the gate holds the skeleton);
      // the global error link owns surfacing the failure.
    }).catch(() => {});
  }, [loading, pool, fetchMore, teamId]);

  return useMemo(
    () => ({
      users: connectionRows(pool),
      ready: !!pool && pool.edges.length >= pool.total,
    }),
    [pool],
  );
}

function buildOrgChartItems(users: OrgChartUser[]): OrgChartUserItem[] {
  // The query already filters server-side; this re-drop catches a cached row
  // whose status a mutation result has since flipped (User is normalized, so
  // suspending a member updates these rows in place).
  const charted = users.filter((u) => CHARTED_STATUSES.has(u.memberStatus));
  const ids = new Set(charted.map((u) => u.userId));
  return charted.map((u) => ({
    id: u.userId,
    // A manager outside the charted set (or unset) makes this member a root.
    parentId: u.managerId && ids.has(u.managerId) ? u.managerId : null,
    ...(u.name ? { label: u.name } : {}),
    data: { kind: "user", user: u },
  }));
}

export interface FullOrgChart {
  items: OrgChartUserItem[];
  /** The "No manager" bucket starts collapsed (shows "+N"). */
  defaultCollapsedIds: string[];
  /** False while the pool is still assembling — gate the canvas on it. */
  ready: boolean;
}

/**
 * The full org chart page's data. Manager-less members who ALSO have no
 * reports (pure orphans) are bucketed under a single collapsed "No manager"
 * node, so the canvas isn't littered with disconnected cards. Manager-less
 * members who DO have reports stay as their own top-level trees — hiding an
 * intact subtree behind a count would be worse than showing it.
 */
export function useFullOrgChart(): FullOrgChart {
  const { users, ready } = useChartedUsers(false);
  return useMemo(() => {
    const base = buildOrgChartItems(users);
    const hasReports = new Set(
      base.flatMap((item) => (item.parentId ? [item.parentId] : [])),
    );
    const orphanLeaves = base.filter(
      (item) => item.parentId === null && !hasReports.has(item.id),
    );

    // Only bucket when it actually declutters — a lone orphan just stays a root.
    if (orphanLeaves.length < 2) {
      return { items: base, defaultCollapsedIds: [], ready };
    }

    const orphanIds = new Set(orphanLeaves.map((item) => item.id));
    const items: OrgChartUserItem[] = [
      {
        id: NO_MANAGER_ID,
        parentId: null,
        variant: "placeholder",
        label: "No manager",
        data: { kind: "placeholder" },
      },
      ...base.map((item) =>
        orphanIds.has(item.id) ? { ...item, parentId: NO_MANAGER_ID } : item,
      ),
    ];
    return { items, defaultCollapsedIds: [NO_MANAGER_ID], ready };
  }, [users, ready]);
}

/**
 * The team chart's slice: the team's charted members, treed by `managerId`
 * restricted to in-team pairs. A manager outside the team (or unset) makes
 * the member a root — an out-of-team manager is not "no manager", so this
 * scope has no placeholder bucket: a run of roots is the honest picture of a
 * team with no internal reporting lines.
 */
export function useTeamOrgChart(teamId: string): FullOrgChart {
  const { users, ready } = useChartedUsers(false, teamId);
  return useMemo(
    () => ({
      items: buildOrgChartItems(users),
      defaultCollapsedIds: [],
      ready,
    }),
    [users, ready],
  );
}

export interface ReportingLine {
  /** The member's slice of the chart: their manager (one level up), the
   *  member, and everyone below them. */
  items: OrgChartUserItem[];
  /** Headcount of the slice (manager + member + all reports) — the
   *  "N people in your reporting line" banner copy. */
  count: number;
  /** Direct reports who have reports of their own start collapsed, so the
   *  mini chart opens one level deep and expands on demand. */
  defaultCollapsedIds: string[];
  /** False while the pool is still assembling — gate the mini chart on it. */
  ready: boolean;
}

/** The profile island's scoped chart: one level up, all the way down. */
export function useReportingLine(userId: string | null): ReportingLine {
  // No target, no query — the profile host mounts this hook app-wide with a
  // null id until a profile first opens.
  const { users, ready } = useChartedUsers(userId === null);
  return useMemo(() => {
    const empty: ReportingLine = {
      items: [],
      count: 0,
      defaultCollapsedIds: [],
      ready,
    };
    if (!userId) return empty;

    const all = buildOrgChartItems(users);
    const byId = new Map(all.map((item) => [item.id, item]));
    const self = byId.get(userId);
    if (!self) return empty;

    const childrenOf = new Map<string, OrgChartUserItem[]>();
    for (const item of all) {
      if (item.parentId === null) continue;
      const siblings = childrenOf.get(item.parentId);
      if (siblings) siblings.push(item);
      else childrenOf.set(item.parentId, [item]);
    }

    const items: OrgChartUserItem[] = [];
    const manager = self.parentId ? byId.get(self.parentId) : undefined;
    if (manager) {
      items.push({ ...manager, parentId: null });
    } else {
      // No manager: a "No manager" stub sits above the focused person so the
      // dashed context line always has a labeled origin, mirroring ClickUp.
      items.push({
        id: NO_MANAGER_ID,
        parentId: null,
        variant: "placeholder",
        label: "No manager",
        data: { kind: "placeholder" },
      });
    }
    // Dashed edge = the "context" line up to the manager (or the stub),
    // de-emphasizing the chain above the member being viewed.
    items.push({
      ...self,
      parentId: manager?.id ?? NO_MANAGER_ID,
      dashedEdge: true,
    });

    const defaultCollapsedIds: string[] = [];
    const seen = new Set(items.map((item) => item.id));
    const queue = [...(childrenOf.get(userId) ?? [])];
    while (queue.length > 0) {
      const item = queue.shift()!;
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      items.push(item);
      const reports = childrenOf.get(item.id) ?? [];
      if (item.parentId === userId && reports.length > 0) {
        defaultCollapsedIds.push(item.id);
      }
      queue.push(...reports);
    }

    // Count real members only — the "No manager" stub isn't a person.
    const count = items.filter((item) => item.data.kind === "user").length;
    return { items, count, defaultCollapsedIds, ready };
  }, [users, ready, userId]);
}
